/**
 * The always-on HUD under the top bar (hud/TopBar): toasts, the eliminated
 * banner, and the bottom dock: the placement or targeting banner, else the
 * selection's (SelectionDock).
 */
import type { BuildingType, Command } from '@/game/sim';
import { cityName } from '@/game/view/cities';
import { TOAST_ICONS, type Toast } from '@/game/view/commandFeedback';
import { type PlacementRefusal, placementBanner, placementHint } from '@/game/view/reasons';
import { useSimFrame } from './app/simFrame';
import { SelectionDock } from './hud/SelectionDock';
import { type UiStore, useUi } from './ui/store';

interface HudProps {
  dispatch: (c: Command) => void;
  selectedUnitIds: readonly string[];
  /**
   * The input store: the tile under the cursor (a walking settler's Found
   * City hint and the placement banner are for it), read only while one of
   * them shows, so plain hovering doesn't re-render the HUD.
   */
  ui: UiStore;
  /** Short messages for refused clicks (warn) and placed sites (ok), newest last. */
  toasts: readonly Toast[];
  placementMode: BuildingType | null;
  /** The city whose territory placement is limited to. */
  placementCityId: string | null;
  /** The last refused placement click (tile + reason), shown while hovering that tile. */
  placementError: PlacementRefusal | null;
  onCancelPlacement: () => void;
  /**
   * A tile-targeting mode (a watchtower's watch spot, a settler's founding
   * spot): its banner (`refused`: the last click's reason) replaces the
   * command bar until a tile is picked or it's cancelled.
   */
  targeting?: { text: string; refused: boolean } | null;
  onCancelTargeting?: () => void;
  /** Enter the settler's "Found city at…" targeting mode. */
  onFoundAt?: (unitId: string) => void;
  /** Enter the attack-move targeting mode. */
  onAttackMove?: () => void;
}

export function Hud({
  dispatch,
  selectedUnitIds,
  ui,
  toasts,
  placementMode,
  placementCityId,
  placementError,
  onCancelPlacement,
  targeting = null,
  onCancelTargeting,
  onFoundAt,
  onAttackMove,
}: HudProps) {
  const { view: state, playerId, playerName, rules, cityNumbers } = useSimFrame();
  const names = useUi(ui, (st) => st.names);
  // One dock in the bottom slot: the Upgrades dock takes it.
  const upgradesOpen = useUi(ui, (st) => st.upgradesOpen);
  const eliminated = state.players[playerId]?.eliminated ?? false;
  // The placement banner: the hint, or why a click on the hovered tile was
  // refused (the hovered tile read only while placing).
  const placementCity = placementCityId ? state.buildings[placementCityId] : undefined;
  const hoveredKey = useUi(ui, (st) => (placementMode !== null ? st.hoveredKey : null));
  const placementBannerText = placementMode
    ? placementBanner(
        placementHint(
          rules,
          placementMode,
          placementCity
            ? cityName(placementCity, playerId, cityNumbers, playerName, names)
            : undefined
        ),
        placementError,
        hoveredKey
      )
    : null;

  return (
    <>
      {/* Toasts (commandFeedback): icon then text, under the timer and the attack banner */}
      {toasts.length > 0 && (
        <div className="pointer-events-none absolute left-1/2 top-24 z-20 flex -translate-x-1/2 flex-col items-center gap-1">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={`pill inline-flex items-center gap-1.5 px-4 py-1.5 text-xs ${t.tone === 'ok' ? 'text-ok' : 'text-warn'}`}
            >
              <span aria-hidden="true">{TOAST_ICONS[t.tone]}</span>
              {t.text}
              {t.count > 1 ? <span className="font-semibold tabular-nums">×{t.count}</span> : null}
            </div>
          ))}
        </div>
      )}

      {/* D. Eliminated banner */}
      {eliminated && (
        <div className="pill absolute bottom-3 left-1/2 z-10 -translate-x-1/2 border-danger/40 bg-danger-surface px-4 py-2 text-sm">
          You have been eliminated — spectating
        </div>
      )}

      {/* E. Placement banner */}
      {placementBannerText && !eliminated && (
        <div className="pill hud-dock flex items-center gap-3 rounded-2xl py-1.5 pl-4 pr-1.5 text-sm">
          <span className={placementBannerText.refused ? 'text-danger' : undefined}>
            {placementBannerText.text}
          </span>
          <button
            type="button"
            onClick={onCancelPlacement}
            className="btn btn-secondary h-7 px-3 text-xs"
          >
            Cancel
          </button>
        </div>
      )}

      {/* E2. Targeting banner (watch spot, founding spot) */}
      {targeting && !eliminated && placementMode === null && (
        <div className="pill hud-dock flex items-center gap-3 rounded-2xl py-1.5 pl-4 pr-1.5 text-sm">
          <span className={targeting.refused ? 'text-danger' : undefined}>{targeting.text}</span>
          <button
            type="button"
            onClick={onCancelTargeting}
            className="btn btn-secondary h-7 px-3 text-xs"
          >
            Cancel
          </button>
        </div>
      )}

      {/* F. Bottom-center command bar */}
      <SelectionDock
        dispatch={dispatch}
        selectedUnitIds={selectedUnitIds}
        ui={ui}
        hidden={eliminated || placementMode !== null || !!targeting || upgradesOpen}
        onFoundAt={onFoundAt}
        onAttackMove={onAttackMove}
      />
    </>
  );
}
